import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createBlock as _createBlock, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, resolveDirective as _resolveDirective, Teleport as _Teleport } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "col-icon-left"
}
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = { class: "col-main" }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["value"]
const _hoisted_7 = {
  key: 1,
  class: "col-icon-right"
}
const _hoisted_8 = { class: "input-icon--right" }
const _hoisted_9 = {
  key: 2,
  class: "col-chevron"
}
const _hoisted_10 = { class: "input-icon--dropdown" }
const _hoisted_11 = {
  key: 3,
  class: "col-check"
}
const _hoisted_12 = { class: "icon" }
const _hoisted_13 = { class: "icon" }
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = ["data-transform"]
const _hoisted_16 = { key: 0 }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = ["innerHTML"]

import Tooltip from '@/components/Tooltip.vue';
import {
  computed,
  ref,
  watch,
  nextTick,
} from 'vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faSpinner,
  faCheck,
  faExclamation,
  faSearch,
} from '@fortawesome/pro-solid-svg-icons';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';

interface GridItems {
  iconLeft?: string
  main: string
  loader?: string
  chevron: string
  check?: string
}

export default /*#__PURE__*/_defineComponent({
  __name: 'SelectField',
  props: /*#__PURE__*/_mergeModels({
    search: { type: Boolean },
    name: {},
    label: { default: '&nbsp;' },
    options: { default: () => [] },
    isValid: { type: Boolean },
    validColor: {},
    readonly: { type: Boolean },
    disabled: { type: Boolean },
    hideLabel: { type: Boolean },
    hideSelectedOption: { type: Boolean },
    fixSelectedOption: { type: Boolean },
    showLoader: { type: Boolean },
    clearable: { type: Boolean },
    customListClass: {},
    iconLeft: {}
  }, {
    "modelValue": {},
    "modelModifiers": {},
    "error": {},
    "errorModifiers": {},
  }),
  emits: ["update:modelValue", "update:error"],
  setup(__props: any) {

library.add(faCheck, faChevronDown, faSearch, faSpinner, faExclamation);

const modelValue = _useModel(__props, "modelValue");
const error = _useModel<undefined | string | string[]>(__props, 'error');
const props = __props;

const root = ref();
const selectFieldList = ref();
const searchInput = ref();

const componentError = ref(error.value);
const isOpen = ref(false);
const searchFor = ref<string>('');
watch(() => error.value, () => {
  componentError.value = error.value;
}, {
  immediate: true,
  deep: true,
});
watch(() => modelValue.value, () => {
  error.value = undefined;
}, {
  deep: true,
});

const errorMessage = computed<string | undefined>(() => {
  if (Array.isArray(componentError.value)) {
    if (componentError.value[0]) {
      return `${componentError.value[0]}`.trim();
    }
  } else if (componentError.value) {
    return `${componentError.value}`.trim();
  }

  return undefined;
});
const hasError = computed<boolean>(() => errorMessage.value !== undefined);

const showValidCheck = computed(() => props.isValid);
const validCheckColor = computed(() => {
  let colors = Array.isArray(props.validColor) ? props.validColor : [props.validColor];
  colors = colors.filter((color) => !!color).map((color) => `${color}`.trim());

  if (colors.length >= 2) {
    return `linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 100%)`;
  }

  return colors[0];
});

const showInvalidCross = computed(() => errorMessage.value !== undefined && !showValidCheck.value);

const getOptionValue = ($option: string | number | Record<string, unknown>)
    : string | number => {
  if (typeof $option === 'object') {
    return $option.value as string | number;
  }

  return $option;
};

const getOptionLabel = ($option: string | number | Record<string, unknown>) => {
  if (typeof $option === 'object') {
    return $option.label;
  }

  return $option;
};

const getOptionKey = ($option: string | number | Record<string, unknown>) => {
  let $label = $option as string;

  if (typeof $option === 'object') {
    $label = $option.label as string;
  }

  return `${$label}`.replace(/[\W_]+/g, ' ');
};

const selectedLabel = computed(() => {
  let $label = null;

  const $selectedOption = [...props.options].filter(($option) => `${getOptionValue($option as string | Record<string, string>)}` === `${modelValue.value}`);
  if ($selectedOption.length) {
    $label = getOptionLabel($selectedOption[0] as string | Record<string, string>);
  }

  return $label;
});

const scrollEventKeys = [37, 38, 39, 40];
const scrollEventPreventDefault = (e: Event) => {
  if (!!e.composedPath && e.composedPath().includes(selectFieldList.value)) {
    const isElementScrollable = (selectFieldList.value?.querySelector('ul')?.scrollHeight || 0) > (selectFieldList.value?.querySelector('ul')?.clientHeight || 0);

    if (isElementScrollable) return;
  }

  e.preventDefault();
};
const scrollEventPreventDefaultScrollKeys = (e: KeyboardEvent) => {
  if (!!e.composedPath && e.composedPath().includes(selectFieldList.value)) {
    return;
  }

  if (scrollEventKeys.includes(e.keyCode)) {
    e.preventDefault();
  }
};

let scrollEventSupportsPassive = false;
try {
  window.addEventListener('checkSupportsPassive', null as any, Object.defineProperty({}, 'passive', {
    get: () => {
      scrollEventSupportsPassive = true;
      return false;
    },
  }) as AddEventListenerOptions);
} catch (e) {
  // do nothing if this doesn't work
}

const wheelOpt = scrollEventSupportsPassive ? { passive: false } : false;
const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';

const disableScrolling = (): void => {
  window.addEventListener('DOMMouseScroll', scrollEventPreventDefault, false);
  window.addEventListener(wheelEvent, scrollEventPreventDefault, wheelOpt as AddEventListenerOptions);
  window.addEventListener('touchmove', scrollEventPreventDefault, wheelOpt as AddEventListenerOptions);
  window.addEventListener('keydown', scrollEventPreventDefaultScrollKeys, false);
};

function enableScroll() {
  window.removeEventListener('DOMMouseScroll', scrollEventPreventDefault, false);
  window.removeEventListener(wheelEvent, scrollEventPreventDefault, wheelOpt as AddEventListenerOptions);
  window.removeEventListener('touchmove', scrollEventPreventDefault, wheelOpt as AddEventListenerOptions);
  window.removeEventListener('keydown', scrollEventPreventDefaultScrollKeys, false);
}

const isSafari = navigator.vendor.match(/apple/i)
    && !navigator.userAgent.match(/crios/i)
    && !navigator.userAgent.match(/fxios/i)
    && !navigator.userAgent.match(/Opera|OPT\//);

const headerInitialyOpen = ref<string | undefined>('true');
const setListStylesInterval = ref();
const setListStyles = () => {
  if (root.value) {
    const headerOpen = !document.querySelector('header.slide-up') ? 'true' : 'false';
    if (headerOpen !== headerInitialyOpen.value && isSafari) {
      headerInitialyOpen.value = undefined;
    }
    const rootPosition = root.value.getBoundingClientRect();
    const body = document.querySelector('body');

    const bottomOffset = rootPosition.bottom;
    let leftOffset = rootPosition.left;
    let widthOffset = rootPosition.width;

    const calculatorNode: Element | undefined = [...document.querySelectorAll('.calculator')].find((calcNode: Node) => calcNode.contains(root.value));
    const calculatorMainRow = calculatorNode?.querySelector('.row.row-main');

    if (calculatorMainRow) {
      let parentCol = root.value.parentNode;

      while (parentCol.parentNode && parentCol.parentNode !== calculatorMainRow) {
        parentCol = parentCol.parentNode;
      }

      if (parentCol) {
        const parentColStyles = window.getComputedStyle(parentCol, null);
        const parentColPaddingLeft = parseInt(parentColStyles.getPropertyValue('padding-left'), 10);
        const parentColPaddingRight = parseInt(parentColStyles.getPropertyValue('padding-right'), 10);

        leftOffset -= parentColPaddingLeft + 1;
        widthOffset += parentColPaddingLeft + parentColPaddingRight + 2;
      }
    }

    body?.style.setProperty('--select-options-list-position', 'fixed');
    body?.style.setProperty('--select-options-list-top', `${bottomOffset}px`);
    body?.style.setProperty('--select-options-list-left', `${leftOffset}px`);
    body?.style.setProperty('--select-options-list-width', `${widthOffset}px`);
  }
};

const removeListStyles = () => {
  const body = document.querySelector('body');

  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-top');
  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-left');
  // eslint-disable-next-line no-unused-expressions
  body?.style.removeProperty('--select-options-list-width');

  if (setListStylesInterval.value) {
    clearInterval(setListStylesInterval.value);
    setListStylesInterval.value = undefined;
  }

  enableScroll();
};

const closeMenuList = () => {
  setTimeout(() => {
    isOpen.value = false;
    removeListStyles();
    searchFor.value = '';
    window.removeEventListener('scroll', setListStyles, {
      passive: true,
    } as EventListenerOptions);
    if (searchInput.value) {
      searchInput.value.removeEventListener('focusout', closeMenuList);
    }
  }, 150);
};

const ifClickedOutside = (event: MouseEvent) => {
  if (root.value) {
    const withinBoundaries = event.composedPath().includes(root.value);

    if (!withinBoundaries) {
      closeMenuList();
    }
  }
};

const openSelect = () => {
  if (props.options.length > 1) {
    headerInitialyOpen.value = !document.querySelector('header.slide-up') ? 'true' : 'false';
    setListStyles();
    if (isSafari) {
      setListStylesInterval.value = setInterval(setListStyles, 75);
      window.addEventListener('scroll', setListStyles, {
        passive: true,
      } as EventListenerOptions);
    }
    disableScrolling();
    nextTick(() => {
      isOpen.value = true;

      nextTick(() => {
        document.addEventListener('click', ifClickedOutside);
        if (searchInput.value) {
          searchInput.value.focus({
            preventScroll: true,
          });

          searchInput.value.addEventListener('focusout', closeMenuList);
        }
      });
    });
  }
};

const setValue = ($option: string | Record<string, string>) => {
  if (props.clearable && modelValue.value === getOptionValue($option)) {
    modelValue.value = undefined;
  } else {
    modelValue.value = getOptionValue($option);
  }
  isOpen.value = false;
  removeListStyles();
  searchFor.value = '';
};

watch(() => isOpen.value, ($isOpen) => {
  if (!$isOpen) {
    document.removeEventListener('click', ifClickedOutside);
  }
});

const searchedOptions = computed(() => {
  if (`${searchFor.value || ''}`.trim() === '') return [...props.options];

  return [...props.options].filter(($option) => {
    const $optionVal = getOptionValue($option as string | Record<string, string>);
    const $optionLabel = getOptionLabel($option as string | Record<string, string>);

    const valueIncludesSearch = `${$optionVal}`
      .toLowerCase()
      .includes((searchFor.value || '').toLowerCase());
    const labelIncludesSearch = `${$optionLabel}`
      .toLowerCase()
      .includes((searchFor.value || '').toLowerCase());

    return valueIncludesSearch || labelIncludesSearch;
  });
});

const possibleValues = computed(() => Object.values(props.options).map((option: any) => (typeof option === 'object' ? option.value : option)));
const filled = computed(() => possibleValues.value.includes(modelValue.value));

const inputGrid = computed<{
  items: GridItems
  columns: string[]
}>(() => {
  const columns = [];
  const items: GridItems = {
    main: '1 / 2',
    chevron: '2 / 3',
  };

  if (props.iconLeft) {
    columns.push('auto');
    items.iconLeft = `${columns.length} / ${columns.length + 1}`;
  }

  columns.push('1fr');
  items.main = `${columns.length} / ${columns.length + 1}`;

  if (props.showLoader) {
    columns.push('auto');
    items.loader = `${columns.length} / ${columns.length + 1}`;
  }

  columns.push('auto');
  items.chevron = `${columns.length} / ${columns.length + 1}`;

  if (showValidCheck.value || showInvalidCross.value) {
    columns.push('auto');
    items.check = `${columns.length} / ${columns.length + 1}`;
  }

  return {
    items,
    columns,
  };
});

const cssVariables = computed<Record<string, string | undefined>>(() => ({
  '--grid-columns': inputGrid.value.columns.join(' '),
  '--grid-column-icon-left': inputGrid.value.items.iconLeft,
  '--grid-column-main': inputGrid.value.items.main,
  '--grid-column-loader': inputGrid.value.items.loader,
  '--grid-column-chevron': inputGrid.value.items.chevron,
  '--grid-column-chevron-padding': inputGrid.value.items.check ? '12px' : '1.875em',
  '--grid-column-check': inputGrid.value.items.check,
}));

return (_ctx: any,_cache: any) => {
  const _component_FontAwesomeIcon = _resolveComponent("FontAwesomeIcon")!
  const _directive_inline_html = _resolveDirective("inline-html")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["select-field-wrapper fs-regular", {
        'open': isOpen.value,
        'hide-selected': _ctx.hideSelectedOption,
        'fix-selected': _ctx.fixSelectedOption,
      }]),
    ref_key: "root",
    ref: root
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["input-field input-field--select", {
          'valid': !_ctx.showLoader && !hasError.value && showValidCheck.value,
          'loading': _ctx.showLoader,
          'icon-right': _ctx.showLoader,
          'filled': filled.value,
          'focused': isOpen.value,
          'disabled': _ctx.disabled,
          'readonly': _ctx.readonly,
          'label-hidden': _ctx.hideLabel,
        }]),
      style: _normalizeStyle(cssVariables.value),
      onClick: openSelect
    }, [
      (_ctx.iconLeft)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("img", {
              src: _ctx.iconLeft,
              alt: `Icon ${selectedLabel.value}`
            }, null, 8, _hoisted_2)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", null, [
          (!_ctx.hideLabel)
            ? (_openBlock(), _createElementBlock("label", {
                key: 0,
                innerHTML: _ctx.label
              }, null, 8, _hoisted_4))
            : _createCommentVNode("", true),
          _createElementVNode("div", {
            class: "input-label",
            innerHTML: selectedLabel.value || ' '
          }, null, 8, _hoisted_5),
          _createElementVNode("input", {
            type: "text",
            readonly: "",
            value: selectedLabel.value,
            onFocusin: openSelect
          }, null, 40, _hoisted_6)
        ])
      ]),
      (_ctx.showLoader)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("span", _hoisted_8, [
              _createVNode(_component_FontAwesomeIcon, {
                class: "fa-pulse",
                icon: _unref(faSpinner)
              }, null, 8, ["icon"])
            ])
          ]))
        : _createCommentVNode("", true),
      (_ctx.options.length > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createElementVNode("span", _hoisted_10, [
              _createVNode(_component_FontAwesomeIcon, { icon: _unref(faChevronDown) }, null, 8, ["icon"])
            ])
          ]))
        : _createCommentVNode("", true),
      (showValidCheck.value || showInvalidCross.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            (showValidCheck.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: "valid-check",
                  style: _normalizeStyle({
              background: validCheckColor.value,
            })
                }, [
                  _createElementVNode("div", _hoisted_12, [
                    _createVNode(_component_FontAwesomeIcon, { icon: _unref(faCheck) }, null, 8, ["icon"])
                  ])
                ], 4))
              : (_openBlock(), _createBlock(Tooltip, {
                  key: 1,
                  class: "invalid-check",
                  "is-error": ""
                }, {
                  title: _withCtx(() => [
                    _createElementVNode("span", { innerHTML: errorMessage.value }, null, 8, _hoisted_14)
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_13, [
                      _createVNode(_component_FontAwesomeIcon, { icon: _unref(faExclamation) }, null, 8, ["icon"])
                    ])
                  ]),
                  _: 1
                }))
          ]))
        : _createCommentVNode("", true)
    ], 6),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "selectFieldList",
            ref: selectFieldList,
            class: _normalizeClass(["select-field-list font-regular", { 'search': _ctx.search, [_ctx.customListClass || 'undef']: !!_ctx.customListClass }]),
            "data-transform": headerInitialyOpen.value
          }, [
            (_ctx.search)
              ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                  _createElementVNode("div", null, [
                    _withDirectives(_createElementVNode("input", {
                      type: "text",
                      ref_key: "searchInput",
                      ref: searchInput,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchFor).value = $event))
                    }, null, 512), [
                      [_vModelText, searchFor.value]
                    ])
                  ]),
                  _createElementVNode("div", null, [
                    _createVNode(_component_FontAwesomeIcon, { icon: _unref(faSearch) }, null, 8, ["icon"])
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("ul", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchedOptions.value, (option, index) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: getOptionKey(option),
                  class: _normalizeClass({'selected': getOptionValue(option) === modelValue.value }),
                  onClick: ($event: any) => (setValue(option))
                }, [
                  _renderSlot(_ctx.$slots, "option", _normalizeProps(_guardReactiveProps({
                  option,
                  index,
                  value: getOptionValue(option),
                  key: getOptionKey(option),
                  selected: getOptionValue(option) === modelValue.value
                })), () => [
                    _withDirectives(_createElementVNode("template", {
                      innerHTML: getOptionLabel(option)
                    }, null, 8, _hoisted_18), [
                      [_directive_inline_html]
                    ])
                  ])
                ], 10, _hoisted_17))
              }), 128))
            ])
          ], 10, _hoisted_15))
        : _createCommentVNode("", true)
    ]))
  ], 2))
}
}

})